import { create } from 'zustand';

// 从 zustand persist 存储中获取 token
const getAuthToken = (): string | null => {
  const userStorage = localStorage.getItem('user-storage');
  if (userStorage) {
    try {
      const parsed = JSON.parse(userStorage);
      return parsed?.state?.token || null;
    } catch {
      return null;
    }
  }
  return null;
};

// 解析 JWT 的 exp（毫秒时间戳），解析不出返回 null
export const getTokenExpiryMs = (token?: string | null): number | null => {
  const raw = token ?? getAuthToken();
  if (!raw) return null;
  try {
    const payload = raw.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    );
    return typeof json?.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
};

// 距 token 过期还剩多少毫秒；无法解析时返回 null（视为未知，不阻断业务）
export const getTokenRemainingMs = (): number | null => {
  const expiry = getTokenExpiryMs();
  return expiry === null ? null : expiry - Date.now();
};

type ProcessingStatus = 'idle' | 'uploading' | 'transcribing' | 'transcribed' | 'evaluating' | 'completed' | 'failed';

// 上传通道异常类型：auth=凭证失效（不会自愈，必须重新登录），network=网络/服务端异常（可能自愈）
export type UploadErrorKind = 'auth' | 'network';

// 连续失败多少次后才提示网络类异常（5 秒一次，3 次约 15 秒）
const NETWORK_ALERT_THRESHOLD = 3;

// 录音前预检：凭证剩余不足此时长则不允许开始录音（面试录音上限 3 小时，留 1 小时余量）
const TOKEN_PRECHECK_BLOCK_MS = 4 * 60 * 60 * 1000;

// 录音前提醒：凭证剩余不足此时长时允许开始，但需提示（供页面展示）
export const TOKEN_PRECHECK_WARN_MS = 24 * 60 * 60 * 1000;

// 带 HTTP 状态码的错误，便于区分 401 与其他失败
class HttpError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
  }
}

// 待上传分片：每个 5 秒切片独立成项，失败后单独重试，避免累积成大 blob
interface PendingChunk {
  index: number;
  blob: Blob;
  duration: number;
}

let recordingSession = 0;
let startingRecording = false;
let uploadTask: { session: number; promise: Promise<void> } | null = null;

// 处理后端下发的续期令牌。
// 录音期间可能长时间只有这几个 fetch 在跑，若不在此处理，滑动续期就会漏掉整场录音。
// 动态 import 避免与 api/client.ts 形成模块循环依赖。
const handleRefreshedToken = (response: Response, requestToken: string | null): void => {
  const refreshed = response.headers.get('X-Refreshed-Token');
  if (!refreshed) return;
  import('../api/client')
    .then(({ applyRefreshedToken }) => applyRefreshedToken(refreshed, requestToken))
    .catch(() => {
      /* 续期是增强功能，失败不影响录音 */
    });
};

interface RecorderState {
  // 录音状态
  isRecording: boolean;
  isPaused: boolean;
  currentCandidateId: number | null;
  currentStage: string;
  candidateName: string;
  duration: number;
  chunkIndex: number;
  uploadingChunk: boolean;

  // 处理状态
  processingStatus: ProcessingStatus;
  recordingId: number | null;

  // 上传通道健康状态
  uploadErrorKind: UploadErrorKind | null;  // 非空表示上传通道异常，界面必须告警
  uploadFailures: number;                   // 连续失败次数（成功即清零）
  lastUploadOkAt: number | null;            // 最后一次成功上传的时间戳
  savedDuration: number;                    // 已成功上传的时长（秒）
  alertAcknowledged: boolean;               // 用户是否已确认过告警模态

  // 内部状态（不暴露给外部）
  mediaRecorder: MediaRecorder | null;
  audioStream: MediaStream | null;
  audioChunks: Blob[];
  recordedChunks: Blob[];
  pendingChunks: PendingChunk[];            // 等待上传/重试的分片队列
  uploadTimer: ReturnType<typeof setInterval> | null;
  durationTimer: ReturnType<typeof setInterval> | null;
  statusPollTimer: ReturnType<typeof setInterval> | null;

  // 方法
  initRecording: (params: { candidateId: number; stage: string; name: string }) => Promise<boolean>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  stopRecording: () => Promise<void>;
  abortRecording: () => void;
  cleanup: () => void;
  resetProcessingStatus: () => void;

  // 上传异常相关
  markAuthExpired: () => void;              // 供 axios 拦截器在录音中撞 401 时调用
  acknowledgeAlert: () => void;
  exportLocalRecording: () => boolean;      // 把内存中尚未上传的录音导出到本地
  hasUnsavedAudio: () => boolean;

  // 内部方法
  uploadChunk: () => Promise<void>;
  startStatusPolling: () => void;
  stopStatusPolling: () => void;
}

// 辅助函数：上传分片
// 注意：这里刻意使用原生 fetch 而非 api/client.ts 的 axios 实例。
// 走 axios 会命中其 401 拦截器的跳转逻辑，录音中跳转会直接销毁内存里的音频。
const fetchRecording = async (url: string, options: RequestInit, timeoutMs = 60000): Promise<Response> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
};

const uploadRecordingChunk = async (formData: FormData) => {
  const token = getAuthToken();
  const response = await fetchRecording('/api/interview/recording/upload-chunk', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });
  handleRefreshedToken(response, token);
  if (!response.ok) {
    throw new HttpError(response.status, `上传分片失败（HTTP ${response.status}）`);
  }
  return response.json();
};

const getAutoFinishedRecordingId = (response: any): number | null => {
  const result = response?.data;
  if (result?.status === 'recording_auto_finished' && result?.recording_id) {
    return result.recording_id;
  }
  return null;
};

// 辅助函数：完成录音
const finishRecordingApi = async (candidateId: number, stage: string) => {
  const token = getAuthToken();
  const response = await fetchRecording('/api/interview/recording/finish', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ candidate_id: candidateId, stage }),
  }, 600000);
  handleRefreshedToken(response, token);
  if (!response.ok) {
    throw new HttpError(response.status, `完成录音请求失败（HTTP ${response.status}）`);
  }
  return response.json();
};

// 辅助函数：获取录音状态
const getRecordingStatusApi = async (recordingId: number) => {
  const token = getAuthToken();
  const response = await fetchRecording(`/api/interview/recording/status/${recordingId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  handleRefreshedToken(response, token);
  if (!response.ok) {
    throw new HttpError(response.status, `获取录音状态失败（HTTP ${response.status}）`);
  }
  return response.json();
};

// 生成导出文件名：候选人_环节_时间.webm
const buildExportFileName = (candidateName: string, stage: string, mimeType: string): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const safeName = (candidateName || '候选人').replace(/[\\/:*?"<>|]/g, '_');
  const safeStage = (stage || '面试').replace(/[\\/:*?"<>|]/g, '_');
  return `${safeName}_${safeStage}_${ts}.${mimeType.includes('mp4') ? 'm4a' : 'webm'}`;
};

export const useRecorderStore = create<RecorderState>((set, get) => ({
  // 初始状态
  isRecording: false,
  isPaused: false,
  currentCandidateId: null,
  currentStage: '',
  candidateName: '',
  duration: 0,
  chunkIndex: 0,
  uploadingChunk: false,
  processingStatus: 'idle',
  recordingId: null,
  uploadErrorKind: null,
  uploadFailures: 0,
  lastUploadOkAt: null,
  savedDuration: 0,
  alertAcknowledged: false,
  mediaRecorder: null,
  audioStream: null,
  audioChunks: [],
  recordedChunks: [],
  pendingChunks: [],
  uploadTimer: null,
  durationTimer: null,
  statusPollTimer: null,

  // 初始化并开始录音
  initRecording: async ({ candidateId, stage, name }) => {
    if (startingRecording || get().isRecording || get().uploadingChunk || get().processingStatus === 'uploading' || get().hasUnsavedAudio()) {
      alert('请先结束当前录音，并处理或导出尚未保存的音频，再开始新的录音。');
      return false;
    }
    startingRecording = true;
    const session = ++recordingSession;
    try {
      // 检查浏览器支持
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('录音功能需要在 HTTPS 环境下使用，或使用 localhost 访问');
        return false;
      }

      // 录音前预检登录凭证剩余有效期：
      // 面试录音最长 3 小时，凭证若中途过期，之后的分片全部上传失败且无法自愈。
      // 解析不出 exp 时视为未知，不阻断（避免因签发格式变化误伤正常使用）。
      const remainingMs = getTokenRemainingMs();
      if (remainingMs !== null) {
        if (remainingMs <= 0) {
          alert('登录凭证已过期，请重新登录后再开始录音。');
          return false;
        }
        if (remainingMs < TOKEN_PRECHECK_BLOCK_MS) {
          const minutes = Math.max(1, Math.floor(remainingMs / 60000));
          alert(
            `登录凭证即将过期（剩余约 ${minutes} 分钟），录音过程中会中断且无法自动恢复。\n\n` +
            '请先退出登录并重新登录，再开始录音。'
          );
          return false;
        }
      }

      // 请求麦克风权限
      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (session !== recordingSession) {
        audioStream.getTracks().forEach(track => track.stop());
        return false;
      }
      set({ audioStream });

      // 选择支持的音频格式
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/mp4';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = '';
          }
        }
      }

      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(audioStream, options);

      // 监听数据可用事件
      mediaRecorder.ondataavailable = (event) => {
        if (session === recordingSession && event.data.size > 0) {
          set((state) => ({
            audioChunks: [...state.audioChunks, event.data],
            recordedChunks: [...state.recordedChunks, event.data],
          }));
        }
      };

      // 开始录音
      mediaRecorder.start(1000);

      // 开始计时
      const durationTimer = setInterval(() => {
        const state = get();
        if (!state.isPaused) {
          set({ duration: state.duration + 1 });
        }
      }, 1000);

      // 每5秒上传一次切片
      const uploadTimer = setInterval(() => {
        const state = get();
        if (state.audioChunks.length > 0 || state.pendingChunks.length > 0) {
          state.uploadChunk();
        }
      }, 5000);

      set({
        audioStream,
        mediaRecorder,
        currentCandidateId: candidateId,
        currentStage: stage,
        candidateName: name || '候选人',
        isRecording: true,
        isPaused: false,
        duration: 0,
        chunkIndex: 0,
        audioChunks: [],
        recordedChunks: [],
        pendingChunks: [],
        processingStatus: 'idle',
        recordingId: null,
        uploadErrorKind: null,
        uploadFailures: 0,
        lastUploadOkAt: Date.now(),
        savedDuration: 0,
        alertAcknowledged: false,
        durationTimer,
        uploadTimer,
      });

      console.log('[录音] 录音已开始');
      return true;
    } catch (error: any) {
      console.error('[录音] 无法访问麦克风:', error);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        alert('请在浏览器中允许麦克风权限后重试');
      } else if (error.name === 'NotFoundError') {
        alert('未检测到麦克风设备，请检查设备连接');
      } else {
        alert('无法访问麦克风，请检查权限设置');
      }
      get().cleanup();
      return false;
    } finally {
      startingRecording = false;
    }
  },

  // 上传切片
  // 每次调用先把新录到的音频封成一个独立分片入队，再逐个上传队列中的分片。
  // 关键：失败的分片留在队列里单独重试，绝不与后续音频合并成一个大 blob
  // （旧实现每 5 秒把累积内容整包重传，失败 17 分钟即达 17.8MB，最终会撞 nginx 体积上限）。
  uploadChunk: async () => {
    const state = get();
    const session = recordingSession;

    // 把新录到的音频封成独立分片入队
    if (state.audioChunks.length > 0) {
      const blob = new Blob(state.audioChunks, {
        type: state.mediaRecorder?.mimeType || 'audio/webm'
      });
      set((s) => ({
        pendingChunks: [...s.pendingChunks, { index: s.chunkIndex, blob, duration: state.duration }],
        chunkIndex: s.chunkIndex + 1,
        audioChunks: [],
      }));
    }

    if (uploadTask?.session === session) return uploadTask.promise;
    if (get().pendingChunks.length === 0) return;

    set({ uploadingChunk: true });

    const task = (async () => {
      try {
        // 逐个上传队列中的分片，成功即出队；遇到失败立即中断本轮，保留剩余分片下轮重试
        while (session === recordingSession) {
          const queue = get().pendingChunks;
          if (queue.length === 0) break;

          const chunk = queue[0];
          const s = get();

          const formData = new FormData();
          formData.append('candidate_id', String(s.currentCandidateId));
          formData.append('stage', s.currentStage);
          formData.append('chunk_index', String(chunk.index));
          formData.append('total_chunks', '-1');
          formData.append('chunk_file', chunk.blob, `chunk_${chunk.index}.webm`);

          let response: any;
          try {
            response = await uploadRecordingChunk(formData);
          } catch (error: any) {
            if (session !== recordingSession) return;
            const kind: UploadErrorKind = error?.status === 401 ? 'auth' : 'network';
            set((prev) => ({
              uploadFailures: prev.uploadFailures + 1,
              // 401 立即告警（凭证问题不会自愈）；网络类异常连续失败达阈值才告警，避免偶发抖动误报
              uploadErrorKind:
                kind === 'auth' || prev.uploadFailures + 1 >= NETWORK_ALERT_THRESHOLD
                  ? kind
                  : prev.uploadErrorKind,
            }));
            console.error(`[录音] 分片 ${chunk.index} 上传失败（累计失败 ${get().uploadFailures} 次）:`, error);
            break;
          }

          if (session !== recordingSession) return;
          const autoFinishedRecordingId = getAutoFinishedRecordingId(response);
          if (autoFinishedRecordingId) {
            console.warn('[录音] 录音超过3小时，后端已自动结束并开始处理');

            const cur = get();
            if (cur.uploadTimer) {
              clearInterval(cur.uploadTimer);
            }
            if (cur.durationTimer) {
              clearInterval(cur.durationTimer);
            }

            if (cur.mediaRecorder) {
              cur.mediaRecorder.ondataavailable = null;
              if (cur.mediaRecorder.state !== 'inactive') {
                cur.mediaRecorder.stop();
              }
            }

            if (cur.audioStream) {
              cur.audioStream.getTracks().forEach(track => track.stop());
            }

            set({
              isRecording: false,
              isPaused: false,
              uploadingChunk: false,
              audioChunks: [],
              pendingChunks: [],
              uploadTimer: null,
              durationTimer: null,
              mediaRecorder: null,
              audioStream: null,
              processingStatus: 'transcribing',
              recordingId: autoFinishedRecordingId,
              uploadErrorKind: null,
              uploadFailures: 0,
            });

            get().startStatusPolling();
            return;
          }

          // 上传成功：出队并清除异常态
          set((prev) => ({
            pendingChunks: prev.pendingChunks.filter((c) => c.index !== chunk.index),
            uploadFailures: 0,
            uploadErrorKind: null,
            lastUploadOkAt: Date.now(),
            savedDuration: Math.max(prev.savedDuration, chunk.duration),
            alertAcknowledged: false,
          }));

          console.log(`[录音] 分片 ${chunk.index} 上传成功，队列剩余 ${get().pendingChunks.length}`);
        }
      } finally {
        if (session === recordingSession) set({ uploadingChunk: false });
      }
    })();
    uploadTask = { session, promise: task };
    try {
      await task;
    } finally {
      if (uploadTask?.promise === task) uploadTask = null;
    }
  },

  // 暂停录音
  pauseRecording: () => {
    const { mediaRecorder } = get();
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.pause();
    }
    set({ isPaused: true });
    console.log('[录音] 录音已暂停');
  },

  // 继续录音
  resumeRecording: () => {
    const { mediaRecorder } = get();
    if (mediaRecorder && mediaRecorder.state === 'paused') {
      mediaRecorder.resume();
    }
    set({ isPaused: false });
    console.log('[录音] 录音已继续');
  },

  // 结束录音
  stopRecording: async () => {
    const state = get();
    if (!state.isRecording && state.processingStatus === 'uploading') return;
    const session = recordingSession;
    console.log('[录音Store] 开始停止录音...');

    const savedCandidateId = state.currentCandidateId;
    const savedStage = state.currentStage;

    // 清除定时器
    if (state.uploadTimer) {
      clearInterval(state.uploadTimer);
    }
    if (state.durationTimer) {
      clearInterval(state.durationTimer);
    }

    set({
      isRecording: false,
      isPaused: false,
      uploadTimer: null,
      durationTimer: null,
      processingStatus: 'uploading',
    });

    if (state.mediaRecorder && state.mediaRecorder.state !== 'inactive') {
      const mediaRecorder = state.mediaRecorder;
      await new Promise<void>((resolve) => {
        mediaRecorder.addEventListener('stop', () => resolve(), { once: true });
        mediaRecorder.stop();
      });
    }
    if (session !== recordingSession) return;

    // 把剩余音频入队并把整个队列传完（uploadChunk 内部逐个上传、失败保留）
    await get().uploadChunk();
    if (session !== recordingSession) return;

    // uploadChunk 可能已走「超过3小时自动结束」分支：后端已建好录音记录并开始处理，
    // 此时绝不能再调 finish，否则会重复生成一条录音记录
    if (get().recordingId) {
      console.log('[录音Store] 后端已自动结束录音，跳过 finish');
      return;
    }

    // 停止音频流
    if (state.audioStream) {
      state.audioStream.getTracks().forEach(track => track.stop());
    }

    // 注意：pendingChunks 不在此处清空。
    // 若仍有分片没传上去，它是本地唯一副本，要留给用户导出，只能由 abortRecording/cleanup 显式丢弃。
    set({
      mediaRecorder: null,
      audioStream: null,
    });

    // 队列没清空说明有分片上传失败，此时调 finish 只会合并出一段残缺录音，直接转失败态
    const leftover = get().pendingChunks.length;
    if (leftover > 0) {
      console.error(`[录音Store] 仍有 ${leftover} 个分片未上传成功，不触发合并`);
      set({ processingStatus: 'failed' });
      return;
    }

    if (savedCandidateId) {
      try {
        set({ processingStatus: 'transcribing' });
        const response = await finishRecordingApi(savedCandidateId, savedStage);
        if (session !== recordingSession) return;

        if (response.data && response.data.recording_id) {
          set({ recordingId: response.data.recording_id, recordedChunks: [] });
          get().startStatusPolling();
        } else {
          set({ processingStatus: 'failed', uploadErrorKind: 'network' });
        }
      } catch (error: any) {
        if (session !== recordingSession) return;
        console.error('[录音Store] 完成录音请求失败:', error);
        set({
          processingStatus: 'failed',
          uploadErrorKind: error?.status === 401 ? 'auth' : 'network',
        });
      }
    }
  },

  // 标记凭证失效：供 axios 拦截器在录音进行中撞 401 时调用，替代跳转登录页
  markAuthExpired: () => {
    const state = get();
    if (!state.isRecording && !state.uploadingChunk && !state.hasUnsavedAudio()) return;
    set({ uploadErrorKind: 'auth' });
  },

  // 用户确认告警模态（模态关闭，但顶部告警条仍在）
  acknowledgeAlert: () => set({ alertAcknowledged: true }),

  // 是否还有尚未成功上传的音频
  hasUnsavedAudio: () => {
    const state = get();
    return state.pendingChunks.length > 0 || state.audioChunks.length > 0 ||
      (state.recordedChunks.length > 0 && !state.recordingId);
  },

  // 导出当前录音的完整本地副本，保留开头的容器信息以保证文件可播放
  exportLocalRecording: () => {
    const state = get();
    const blobs = state.recordedChunks;
    if (blobs.length === 0) {
      console.warn('[录音] 没有可导出的音频');
      return false;
    }

    try {
      const mimeType = state.mediaRecorder?.mimeType || blobs[0].type || 'audio/webm';
      const merged = new Blob(blobs, { type: mimeType });
      const url = URL.createObjectURL(merged);
      const a = document.createElement('a');
      a.href = url;
      a.download = buildExportFileName(state.candidateName, state.currentStage, mimeType);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // 延迟释放，确保下载已经启动
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      console.log('[录音] 已导出完整录音到本地');
      return true;
    } catch (error) {
      console.error('[录音] 导出失败:', error);
      return false;
    }
  },

  // 强制终止录音：只停止本地录音与上传，不触发后端 finish
  abortRecording: () => {
    const state = get();
    recordingSession += 1;
    uploadTask = null;

    if (state.uploadTimer) {
      clearInterval(state.uploadTimer);
    }
    if (state.durationTimer) {
      clearInterval(state.durationTimer);
    }
    if (state.statusPollTimer) {
      clearInterval(state.statusPollTimer);
    }

    if (state.mediaRecorder && state.mediaRecorder.state !== 'inactive') {
      try {
        state.mediaRecorder.ondataavailable = null;
        state.mediaRecorder.stop();
      } catch (error) {
        console.error('[录音Store] 强制终止 mediaRecorder 失败:', error);
      }
    }

    if (state.audioStream) {
      state.audioStream.getTracks().forEach(track => track.stop());
    }

    set({
      isRecording: false,
      isPaused: false,
      currentCandidateId: null,
      currentStage: '',
      candidateName: '',
      duration: 0,
      chunkIndex: 0,
      uploadingChunk: false,
      processingStatus: 'idle',
      recordingId: null,
      uploadErrorKind: null,
      uploadFailures: 0,
      lastUploadOkAt: null,
      savedDuration: 0,
      alertAcknowledged: false,
      mediaRecorder: null,
      audioStream: null,
      audioChunks: [],
      recordedChunks: [],
      pendingChunks: [],
      uploadTimer: null,
      durationTimer: null,
      statusPollTimer: null,
    });
  },

  // 开始轮询状态
  startStatusPolling: () => {
    const state = get();
    if (state.statusPollTimer) {
      clearInterval(state.statusPollTimer);
    }

    const timer = setInterval(async () => {
      const { recordingId } = get();
      if (!recordingId) {
        get().stopStatusPolling();
        return;
      }

      try {
        const response = await getRecordingStatusApi(recordingId);
        if (get().recordingId !== recordingId) return;
        const status = response.data?.status;

        if (status) {
          const statusMap: Record<string, ProcessingStatus> = {
            'waiting': 'transcribing',
            'transcribing': 'transcribing',
            'transcribed': 'transcribed',
            'evaluating': 'evaluating',
            'completed': 'completed',
            'failed': 'failed',
          };
          set({ processingStatus: statusMap[status] || status });
        }

        if (['completed', 'failed'].includes(status)) {
          get().stopStatusPolling();
        }
      } catch (error) {
        console.error('[录音] 状态轮询失败:', error);
      }
    }, 2000);

    set({ statusPollTimer: timer });
  },

  // 停止轮询状态
  stopStatusPolling: () => {
    const { statusPollTimer } = get();
    if (statusPollTimer) {
      clearInterval(statusPollTimer);
      set({ statusPollTimer: null });
    }
  },

  // 清理资源
  cleanup: () => {
    get().abortRecording();
  },

  // 重置处理状态
  resetProcessingStatus: () => {
    if (get().hasUnsavedAudio()) return;
    get().stopStatusPolling();
    set({
      processingStatus: 'idle',
      recordingId: null,
      uploadErrorKind: null,
      uploadFailures: 0,
      alertAcknowledged: false,
      pendingChunks: [],
    });
  },
}));
